/**
 * HotUrlRow — one row of a Trending "Hot URLs" feed (bd deepcontext-6pf3).
 *
 * Shared by both feeds on /clusters/trending: the market-wide feed
 * (`GET /api/clusters/urls`) and the drilled-cluster feed (top_urls of
 * `GET /api/clusters/topics/:id`). Raw technical URLs are unreadable as the
 * primary text, so the row leads with the page headline and keeps the
 * protocol-stripped URL as muted secondary text. The colored dot carries the
 * cluster identity — the row deliberately renders no cluster label text.
 */

import React from 'react';

export interface HotUrlRowProps {
    /** Cluster colour for the leading dot. */
    dotColor: string;
    /** Page headline from `url_classifications.title`; may be empty. */
    title?: string | null;
    /** Full URL, hydrated server-side; may be empty. */
    url?: string | null;
    /** Last-resort identifier when there is no url at all. */
    urlHash: string;
    /** Fit score in 0..1, or null when unscored. Ignored when `timeAgo` or `countToday` is set. */
    score: number | null;
    /** Relative-time label (e.g. "12 min ago") shown instead of the score, for "newest first" feeds. */
    timeAgo?: string | null;
    /**
     * Today's ad-request count for this URL — the drilled story panel's
     * primary metric (bd deepcontext-k9sw4). Takes priority over `score` /
     * `timeAgo` when set. Null when the signal read failed.
     */
    countToday?: number | null;
    /** Muted secondary line under the count, e.g. "joined 3h ago". Only rendered alongside `countToday`. */
    joinedLabel?: string | null;
}

/**
 * Display lines for one hot-URL row.
 *
 * - title present            → headline / stripped url
 * - no title but a url       → bare domain / path
 * - neither                  → url_hash / nothing
 */
export function hotUrlDisplayLines(props: {
    title?: string | null;
    url?: string | null;
    urlHash: string;
}): {primary: string; secondary: string} {
    const title = (props.title ?? '').trim();
    const stripped = (props.url ?? '').trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '');

    if (title) return {primary: title, secondary: stripped};
    if (!stripped) return {primary: props.urlHash, secondary: ''};

    const slash = stripped.indexOf('/');
    if (slash === -1) return {primary: stripped, secondary: ''};
    return {primary: stripped.slice(0, slash), secondary: stripped.slice(slash)};
}

const HotUrlRow: React.FC<HotUrlRowProps> = ({dotColor, title, url, urlHash, score, timeAgo, countToday, joinedLabel}) => {
    const {primary, secondary} = hotUrlDisplayLines({title, url, urlHash});
    const hasCountToday = countToday !== undefined;
    const metric = timeAgo != null ? timeAgo : score != null ? `${(score * 100).toFixed(0)}%` : '—';
    return (
        <li className="py-1.5 flex items-center gap-3">
            <span aria-hidden className="w-2 h-2 rounded shrink-0" style={{background: dotColor}} />
            <div className="min-w-0 flex-1">
                <div data-testid="hoturl-title" className="text-sm font-semibold text-ink truncate">
                    {primary}
                </div>
                {secondary ? (
                    <div data-testid="hoturl-url" className="text-xs text-ink-muted truncate">
                        {secondary}
                    </div>
                ) : null}
            </div>
            {hasCountToday ? (
                <div className="shrink-0 text-right">
                    <b data-testid="hoturl-count" className="block text-sm font-extrabold text-ink tabular-nums">
                        {countToday != null ? countToday.toLocaleString() : '—'}
                    </b>
                    {joinedLabel ? (
                        <div data-testid="hoturl-joined" className="text-xs text-ink-muted">
                            {joinedLabel}
                        </div>
                    ) : null}
                </div>
            ) : (
                <b className="text-sm font-extrabold text-ink shrink-0 tabular-nums">
                    {metric}
                </b>
            )}
        </li>
    );
};

export default HotUrlRow;
